/** Looking around and pointing (docs/SPEC.md §7.6, §8.2), the arithmetic:
 *  the person's own view as an offset from the film's camera, the camera it
 *  gives at any moment, the `view` a change carries, and a rectangle marked
 *  on the picture. Nothing here imports three or touches the DOM.
 *
 *  Axes: the film's (Z up, metres), as the sidecar's camera rows are; the
 *  player hands the result to the engine as a scene's camera (`DrawOp`).
 *
 *  The steps of a drag (`orbitStep`, `panStep`, `dollyStep`) are the live
 *  player's (`live/play.ts`, Phase 13), copied: `live/` goes with P25. */
import type { Vec3 } from "./manifest";
import type { CameraPose } from "./play";

/** How the person's view differs from the film's camera: turned around the
 *  point the film looks at, nearer or farther, and the point slid. */
export interface FreeOffset {
  /** Degrees, −180 < yaw ≤ 180, about the vertical through the point. */
  yaw: number;
  /** Degrees added to the view's height angle; the result stays inside
   *  ±`PITCH_MAX`. */
  pitch: number;
  /** The distance to the point as a multiple of the film camera's. */
  dolly: number;
  /** The point slid right (+) and up (+), in film-camera distances. */
  pan: [number, number];
}

/** The film camera's own view. */
export const FREE_ZERO: FreeOffset = {
  yaw: 0,
  pitch: 0,
  dolly: 1,
  pan: [0, 0],
};

export const PITCH_MAX = 85;
export const DOLLY_MIN = 0.05;
export const DOLLY_MAX = 20;
export const PAN_MAX = 50;
/** The lowest the person's view may stand (m), unless the film's own camera
 *  stands lower: never under the floor. */
export const VIEW_FLOOR = 0.02;

const clamp = (x: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, Number.isFinite(x) ? x : 0));

/** An angle in degrees wrapped into (−180, 180]. */
export function wrapDegrees(a: number): number {
  if (!Number.isFinite(a)) return 0;
  const w = ((((a + 180) % 360) + 360) % 360) - 180;
  return (w === -180 ? 180 : w) + 0;
}

export function clampOffset(o: FreeOffset): FreeOffset {
  return {
    yaw: wrapDegrees(o.yaw),
    pitch: clamp(o.pitch, -2 * PITCH_MAX, 2 * PITCH_MAX),
    dolly: Number.isFinite(o.dolly) ? clamp(o.dolly, DOLLY_MIN, DOLLY_MAX) : 1,
    pan: [
      clamp(o.pan[0], -PAN_MAX, PAN_MAX),
      clamp(o.pan[1], -PAN_MAX, PAN_MAX),
    ],
  };
}

/** Whether the offset is the film camera's own view. */
export function isFilmView(o: FreeOffset | null): boolean {
  return (
    o === null ||
    (o.yaw === 0 &&
      o.pitch === 0 &&
      o.dolly === 1 &&
      o.pan[0] === 0 &&
      o.pan[1] === 0)
  );
}

/** A drag that turns the view around the point: dragging right turns the
 *  scene with the hand, dragging down raises the view. `H` is the picture's
 *  height in CSS pixels. */
export function orbitStep(
  o: FreeOffset,
  dxPx: number,
  dyPx: number,
  H: number,
): FreeOffset {
  const h = Math.max(1, H);
  return clampOffset({
    ...o,
    yaw: o.yaw - (360 * dxPx) / h,
    pitch: o.pitch + (180 * dyPx) / h,
  });
}

/** A drag that slides the point so that what is under the hand stays under
 *  it; `fovY` is the camera's vertical field of view, degrees. */
export function panStep(
  o: FreeOffset,
  dxPx: number,
  dyPx: number,
  H: number,
  fovY: number,
): FreeOffset {
  const s = (2 * Math.tan((fovY * Math.PI) / 360) * o.dolly) / Math.max(1, H);
  return clampOffset({ ...o, pan: [o.pan[0] - dxPx * s, o.pan[1] + dyPx * s] });
}

/** Nearer (factor < 1) or farther (factor > 1). */
export function dollyStep(o: FreeOffset, factor: number): FreeOffset {
  return clampOffset({ ...o, dolly: o.dolly * factor });
}

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const length = (a: Vec3): number => Math.hypot(a[0], a[1], a[2]);
const norm = (a: Vec3): Vec3 => {
  const l = length(a);
  return l > 1e-9 ? scale(a, 1 / l) : [0, 1, 0];
};
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

/** The person's camera: the film's camera `base` at a moment with the
 *  offset laid over it. The point looked at is the film camera's `to`, slid
 *  by `pan`; the view turns about the vertical through it (`yaw`), rises or
 *  falls (`pitch`, kept inside ±`PITCH_MAX` of level) and stands `dolly`
 *  times the film camera's distance away; never under the floor unless the
 *  film's own camera is. The lens is the film camera's. */
export function freeCamera(base: CameraPose, o: FreeOffset): CameraPose {
  const d = Math.max(0.05, length(sub(base.to, base.from)));
  const fwd = norm(sub(base.to, base.from));
  let right = cross(fwd, [0, 0, 1]);
  if (length(right) < 1e-6) right = [1, 0, 0];
  right = norm(right);
  const up = norm(cross(right, fwd));
  const to = add(
    base.to,
    add(scale(right, o.pan[0] * d), scale(up, o.pan[1] * d)),
  );
  const back = scale(fwd, -1);
  const az = Math.atan2(back[1], back[0]) + (o.yaw * Math.PI) / 180;
  const el0 = (Math.asin(clamp(back[2], -1, 1)) * 180) / Math.PI;
  const el = (clamp(el0 + o.pitch, -PITCH_MAX, PITCH_MAX) * Math.PI) / 180;
  const r = d * o.dolly;
  const from: Vec3 = [
    to[0] + r * Math.cos(el) * Math.cos(az),
    to[1] + r * Math.cos(el) * Math.sin(az),
    to[2] + r * Math.sin(el),
  ];
  const floor = Math.min(VIEW_FLOOR, base.from[2]);
  if (from[2] < floor) from[2] = floor;
  return { from, to, fovY: base.fovY };
}

/** `(width, height)` of the picture with the longer side 1
 *  (`rigs/camera/views.py::side_ratios`). */
export function sideRatios(aspect: string): [number, number] {
  const [w, h] = aspect.split(":").map(Number);
  if (!(w > 0 && h > 0)) return [1, 1];
  const longest = Math.max(w, h);
  return [w / longest, h / longest];
}

/** The field of view across the longer side for `fovY` across the height
 *  (`manifest.py::fov_from_y`): what `film.json`'s view calls `fov`. */
export function fovAcross(fovY: number, aspect: string): number {
  const [, ay] = sideRatios(aspect);
  return (2 * Math.atan(Math.tan((fovY * Math.PI) / 360) / ay) * 180) / Math.PI;
}

/** The `view` of a change (§8.2): the camera in the film's axes, rounded
 *  to the millimetre, its `fov` across the longer side as `film.json` has
 *  it. */
export interface ChangeView {
  from: Vec3;
  to: Vec3;
  fov: number;
}

const mm = (x: number): number => Math.round(x * 1000) / 1000 + 0;

export function changeView(pose: CameraPose, aspect: string): ChangeView {
  return {
    from: [mm(pose.from[0]), mm(pose.from[1]), mm(pose.from[2])],
    to: [mm(pose.to[0]), mm(pose.to[1]), mm(pose.to[2])],
    fov: Math.round(clamp(fovAcross(pose.fovY, aspect), 5, 120) * 100) / 100,
  };
}

// ------------------------------------------------------------- one object

/** A box in the film's axes. */
export interface Box {
  min: Vec3;
  max: Vec3;
}

/** How much room is left round an object framed alone: its sphere fills
 *  this share of the narrower side. */
export const FRAMED_FILL = 0.8;

/** The view that shows one object alone (P28-T16, a stand-in's picture in
 *  the cast panel): the film camera's own direction, the point looked at
 *  slid onto the box's centre and the camera brought nearer or farther so
 *  that the box's sphere fills `fill` of the narrower side of the picture
 *  (`aspect`) — so a square crop of the middle shows it whole. As an offset
 *  of the film camera (`freeCamera` gives the camera back): `pan` puts the
 *  point on the line from the camera through the centre, `dolly` the
 *  distance. */
export function framingOffset(
  base: CameraPose,
  box: Box,
  aspect: string,
  fill = FRAMED_FILL,
): FreeOffset {
  const d = Math.max(0.05, length(sub(base.to, base.from)));
  const fwd = norm(sub(base.to, base.from));
  let right = cross(fwd, [0, 0, 1]);
  if (length(right) < 1e-6) right = [1, 0, 0];
  right = norm(right);
  const up = norm(cross(right, fwd));
  const centre: Vec3 = [
    (box.min[0] + box.max[0]) / 2,
    (box.min[1] + box.max[1]) / 2,
    (box.min[2] + box.max[2]) / 2,
  ];
  const radius = Math.max(0.02, length(sub(box.max, box.min)) / 2);
  const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  const v = sub(centre, base.to);
  // the narrower side's half angle: the picture's height or its width
  const [aw, ah] = sideRatios(aspect);
  const halfY = (base.fovY * Math.PI) / 360;
  const halfX = Math.atan(Math.tan(halfY) * (aw / ah));
  const half = Math.min(halfY, halfX);
  const distance =
    radius / (Math.max(0.05, Math.min(1, fill)) * Math.sin(half));
  // the camera stands `distance` before the centre: `r` before the point
  const r = distance - dot(v, fwd);
  return clampOffset({
    yaw: 0,
    pitch: 0,
    dolly: r / d,
    pan: [dot(v, right) / d, dot(v, up) / d],
  });
}

// ------------------------------------------------------------------ an area

/** A rectangle of the picture, `[x0, y0, x1, y1]`, 0..1 from the top-left. */
export type Rect = [number, number, number, number];

/** The smallest width and height of a marked area (Phase 13's). */
export const AREA_MIN = 0.02;

const round3 = (x: number): number => Math.round(x * 1000) / 1000 + 0;
const unit = (x: number): number => clamp(x, 0, 1);

/** The rectangle between a press `a` and a release `b` on a picture `w` ×
 *  `h` CSS pixels, ordered and clamped; null when it is under `AREA_MIN`
 *  wide or high (that was a click). */
export function rectBetween(
  a: readonly [number, number],
  b: readonly [number, number],
  w: number,
  h: number,
): Rect | null {
  const width = Math.max(1, w);
  const height = Math.max(1, h);
  const xs = [unit(a[0] / width), unit(b[0] / width)].sort((p, q) => p - q);
  const ys = [unit(a[1] / height), unit(b[1] / height)].sort((p, q) => p - q);
  const rect: Rect = [
    round3(xs[0]),
    round3(ys[0]),
    round3(xs[1]),
    round3(ys[1]),
  ];
  if (rect[2] - rect[0] < AREA_MIN || rect[3] - rect[1] < AREA_MIN) return null;
  return rect;
}

/** §8.2's `area`: `{x, y, w, h}` in picture fractions. */
export function areaOf(r: Rect): {
  x: number;
  y: number;
  w: number;
  h: number;
} {
  return {
    x: r[0],
    y: r[1],
    w: round3(r[2] - r[0]),
    h: round3(r[3] - r[1]),
  };
}
